import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Calendar, Clock, User } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import useSEO, { SITE_URL, SITE_NAME } from "@/lib/useSEO";
import { getBySlug, CATEGORIES, localize } from "@/content/blog";

function fmtDate(iso, lang) {
    try {
        // Treat publishedAt as a calendar date (YYYY-MM-DD), not a UTC timestamp,
        // so it never shifts a day back in negative-UTC timezones.
        const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
        const dt = new Date(y, (m || 1) - 1, d || 1);
        return dt.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
            year: "numeric", month: "long", day: "numeric",
        });
    } catch (_) { return iso; }
}

function categoryLabel(key, lang) {
    return (CATEGORIES[key] && CATEGORIES[key][lang]) || CATEGORIES[key]?.en || key;
}

// Inline segments for paragraphs: plain strings, { text, bold } or
// { text, href } (internal react-router links). No HTML injection.
function Inline({ part }) {
    if (typeof part === "string") return <>{part}</>;
    if (part.href) {
        return (
            <Link
                to={part.href}
                className="text-navy underline decoration-gold/60 underline-offset-4 hover:text-gold transition-colors"
                data-testid={`article-inline-link-${part.href}`}
            >
                {part.text}
            </Link>
        );
    }
    if (part.bold) return <strong className="font-semibold text-navy">{part.text}</strong>;
    return <>{part.text}</>;
}

// Render a single content block. Kept simple, no HTML injection.
function Block({ block }) {
    switch (block.type) {
        case "h2":
            return (
                <h2 className="font-serif text-navy text-2xl sm:text-3xl mt-14 mb-6 tracking-tight leading-tight" data-testid="article-h2">
                    {block.text}
                </h2>
            );
        case "h3":
            return (
                <h3 className="font-serif text-navy text-xl sm:text-2xl mt-10 mb-4 tracking-tight leading-tight" data-testid="article-h3">
                    {block.text}
                </h3>
            );
        case "p":
            return (
                <p
                    className={`text-base sm:text-[1.05rem] leading-[1.85] mb-6 ${block.strong ? "text-navy font-semibold" : "text-navy/80"}`}
                    data-testid="article-p"
                >
                    {block.parts ? block.parts.map((part, i) => <Inline key={i} part={part} />) : block.text}
                </p>
            );
        case "ul":
            return (
                <ul className="mb-8 space-y-3 text-navy/80 text-base sm:text-[1.05rem] leading-relaxed pl-5" data-testid="article-ul">
                    {block.items.map((it, i) => (
                        <li key={i} className="relative pl-4 before:content-[''] before:absolute before:left-0 before:top-[0.7em] before:w-1.5 before:h-1.5 before:bg-gold before:rounded-full">
                            {it}
                        </li>
                    ))}
                </ul>
            );
        case "ol":
            return (
                <ol className="mb-8 space-y-3 text-navy/80 text-base sm:text-[1.05rem] leading-relaxed pl-5 list-decimal marker:text-gold marker:font-serif" data-testid="article-ol">
                    {block.items.map((it, i) => (<li key={i} className="pl-2">{it}</li>))}
                </ol>
            );
        case "quote":
            return (
                <blockquote className="my-10 border-l-2 border-gold pl-6 sm:pl-8 py-2" data-testid="article-quote">
                    <p className="font-serif text-navy text-xl sm:text-2xl italic leading-relaxed">&ldquo;{block.text}&rdquo;</p>
                    {block.cite && <cite className="block mt-4 not-italic text-xs tracking-widest uppercase text-navy/50">— {block.cite}</cite>}
                </blockquote>
            );
        case "callout":
            return (
                <aside className="my-12 bg-navy text-pearl px-8 sm:px-10 py-10" data-testid="article-callout">
                    <p className="overline text-gold/80 mb-3">{block.title}</p>
                    <p className="text-pearl/85 text-base sm:text-lg leading-relaxed mb-6">{block.text}</p>
                    {block.ctaLabel && block.ctaHref && (
                        <Link
                            to={block.ctaHref}
                            className="inline-flex items-center gap-3 border border-gold/60 text-gold px-5 py-2.5 text-[0.65rem] tracking-[0.28em] uppercase hover:bg-gold hover:text-navy transition-colors duration-300"
                            data-testid={`article-callout-cta-${block.ctaHref}`}
                        >
                            {block.ctaLabel}
                            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.25} />
                        </Link>
                    )}
                </aside>
            );
        case "image":
            return (
                <figure className="my-10" data-testid="article-image">
                    <img src={block.src} alt={block.alt} loading="lazy" className="w-full object-cover" />
                    {block.caption && <figcaption className="mt-3 text-xs text-navy/50 italic">{block.caption}</figcaption>}
                </figure>
            );
        default:
            return null;
    }
}

export default function BlogArticle() {
    const { slug } = useParams();
    const { lang } = useLanguage();
    const article = getBySlug(slug);
    const isPublished = Boolean(article && article.status === "published");

    // Derive SEO inputs; when article is missing, use safe placeholders so
    // useSEO can be called unconditionally (React hooks rule). The early
    // return below never actually reaches the render for missing slugs —
    // React Router redirects — but we still need the hook to run in a
    // deterministic order across renders.
    const loc = isPublished ? localize(article, lang) : null;
    const url = isPublished ? `${SITE_URL}/blog/${article.slug}` : `${SITE_URL}/blog`;
    const image = isPublished
        ? (article.featuredImage.ogUrl || article.featuredImage.url)
        : undefined;

    const articleSchema = isPublished
        ? {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              headline: loc.title,
              description: loc.excerpt,
              image: [image],
              datePublished: article.publishedAt,
              dateModified: article.updatedAt,
              author: {
                  "@type": "Organization",
                  name: article.author.name,
                  url: article.author.url,
              },
              publisher: {
                  "@type": "Organization",
                  name: SITE_NAME,
                  url: SITE_URL,
                  logo: {
                      "@type": "ImageObject",
                      url: `${SITE_URL}/brand/pkm-icon.png`,
                  },
              },
              mainEntityOfPage: {
                  "@type": "WebPage",
                  "@id": url,
              },
              articleSection: categoryLabel(article.category, "en"),
              inLanguage: loc._lang === "es" ? "es-MX" : "en-US",
          }
        : null;

    useSEO({
        title: isPublished ? (loc.metaTitle || loc.title) : "Journal",
        description: isPublished ? (loc.metaDescription || loc.excerpt) : "Pacific Key Management editorial journal.",
        path: isPublished ? `/blog/${article.slug}` : "/blog",
        image,
        imageAlt: isPublished ? article.featuredImage.alt : undefined,
        type: isPublished ? "article" : "website",
        keywords: isPublished ? loc.keywords : undefined,
        breadcrumbs: isPublished
            ? [
                  { name: lang === "es" ? "Inicio" : "Home", path: "/" },
                  { name: "Journal", path: "/blog" },
                  { name: loc.title, path: `/blog/${article.slug}` },
              ]
            : undefined,
        schema: articleSchema,
    });

    // Unknown slug OR unpublished draft → redirect to blog index (keeps
    // React Router happy without hitting the global "*" homepage redirect).
    if (!isPublished) {
        return <Navigate to="/blog" replace />;
    }

    return (
        <article data-testid="blog-article" className="bg-pearl">
            {/* Featured image / hero band */}
            <header className="relative h-[52vh] min-h-[380px] w-full overflow-hidden" data-testid="article-hero">
                <div className="absolute inset-0 overflow-hidden">
                    <img
                        src={article.featuredImage.url}
                        alt={article.featuredImage.alt}
                        className="w-full h-full object-cover"
                        loading="eager"
                    />
                    <div className="absolute inset-0 bg-navy/55" />
                    <div className="absolute inset-0 bg-gradient-to-b from-navy/40 via-transparent to-navy/60" />
                </div>
                <div className="relative h-full container-luxe flex flex-col justify-end pb-14 sm:pb-20">
                    <p className="overline text-gold/90 mb-5">{categoryLabel(article.category, lang)}</p>
                    <h1 className="font-serif text-pearl text-3xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight max-w-4xl" data-testid="article-title">
                        {loc.title}
                    </h1>
                    {loc.heroSubtitle && (
                        <p className="mt-5 text-pearl/80 text-base sm:text-lg max-w-3xl leading-relaxed">
                            {loc.heroSubtitle}
                        </p>
                    )}
                </div>
            </header>

            {/* Meta bar */}
            <section className="border-b border-navy/10 py-6" data-testid="article-meta">
                <div className="container-luxe max-w-3xl flex flex-wrap items-center gap-x-8 gap-y-3 text-xs tracking-widest uppercase text-navy/50">
                    <span className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-gold" strokeWidth={1.25} />{article.author.name}</span>
                    <span className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gold" strokeWidth={1.25} />{fmtDate(article.publishedAt, lang)}</span>
                    {article.readingMinutes && (
                        <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-gold" strokeWidth={1.25} />{article.readingMinutes} {lang === "es" ? "min de lectura" : "min read"}</span>
                    )}
                </div>
            </section>

            {/* Body */}
            <section className="py-16 sm:py-20">
                <div className="container-luxe max-w-3xl">
                    {loc._fallback && (
                        <p className="mb-10 text-xs tracking-widest uppercase text-navy/50 border border-navy/10 px-4 py-3 bg-white/60" data-testid="article-lang-notice">
                            {lang === "es" ? "Este artículo está disponible en inglés. La versión en español estará pronto." : ""}
                        </p>
                    )}

                    {/* NOTE: intentionally NOT wrapped in <Reveal> — the body is
                        thousands of px tall, so an IntersectionObserver threshold
                        can never be met and the content would stay at opacity 0. */}
                    <div className="prose-luxe">
                        {(loc.content || []).map((block, i) => (
                            <Block key={i} block={block} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Related internal links */}
            {loc.relatedLinks && loc.relatedLinks.length > 0 && (
                <section className="pb-20" data-testid="article-related">
                    <div className="container-luxe max-w-3xl border-t border-navy/10 pt-10">
                        <p className="overline text-gold/80 mb-5">
                            {lang === "es" ? "Relacionado en Pacific Key Management" : "Related from Pacific Key Management"}
                        </p>
                        <ul className="space-y-3">
                            {loc.relatedLinks.map((link) => (
                                <li key={link.href}>
                                    <Link
                                        to={link.href}
                                        className="inline-flex items-center gap-3 text-navy hover:text-gold transition-colors text-sm sm:text-base"
                                        data-testid={`article-related-${link.href}`}
                                    >
                                        <ArrowRight className="w-4 h-4 text-gold" strokeWidth={1.25} />
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}

            {/* Bottom nav */}
            <section className="bg-navy text-pearl py-14" data-testid="article-bottom-nav">
                <div className="container-luxe flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <Link
                        to="/blog"
                        data-testid="article-back-to-blog"
                        className="inline-flex items-center gap-3 text-pearl/80 hover:text-gold transition-colors text-[0.7rem] tracking-[0.28em] uppercase"
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={1.25} />
                        {lang === "es" ? "Volver al Journal" : "Back to Journal"}
                    </Link>
                    <Link
                        to="/contact"
                        data-testid="article-cta-contact"
                        className="inline-flex items-center gap-3 border border-gold/60 text-gold px-6 py-3.5 text-[0.7rem] tracking-[0.28em] uppercase hover:bg-gold hover:text-navy transition-colors duration-300"
                    >
                        {lang === "es" ? "Solicitar Consulta" : "Request Consultation"}
                        <ArrowRight className="w-4 h-4" strokeWidth={1.25} />
                    </Link>
                </div>
            </section>
        </article>
    );
}
